import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useClient } from '../../../ClientProvider';
import { mergeQueryState } from '../../../mergeState';
import { DecitraitDiseaseId } from '../../../resources/decitrait';
import schemas from '../../../schemas';
import useMutation from '../../../useMutation';
import useQuery from '../../../useQuery';
import { useProductIdMap } from '../../useArrayToMap';
import { decitraitResourceToOptions } from './utils';
const ADD_TREATMENT_SCHEMA = schemas.decitrait.addTreatment;
const ALL_DISEASE_IDS = [DecitraitDiseaseId.mildew, DecitraitDiseaseId.oidium];
const diseaseIdToFieldName = {
    [DecitraitDiseaseId.mildew]: 'mildewProductIds',
    [DecitraitDiseaseId.oidium]: 'oidiumProductIds'
};
export const useDecitraitAddTreatment = ({ orgId, onCreateTreatmentSuccess, onCreateTreatmentError, plotId }) => {
    const client = useClient();
    const [step, setStep] = useState(1);
    const resourcesRequest = useQuery(client.decitrait.getResources({ organizationId: orgId }));
    const resources = resourcesRequest.data;
    const productsRequest = useQuery(client.decitrait.products({ organizationId: orgId }));
    const allProducts = productsRequest.data;
    const [currentDiseaseId, setCurrentDiseaseId] = useState(DecitraitDiseaseId.mildew);
    const currentAvailableProducts = !isNil(allProducts) ? allProducts[currentDiseaseId] : [];
    const [optidoseMutation, optidoseRequest] = useMutation(client.decitrait.optidose(plotId));
    const sensibilityOptions = decitraitResourceToOptions(resources?.sensibility);
    const [productIds, setProductIds] = useState({
        mildewProductIds: [],
        oidiumProductIds: []
    });
    const [treatmentParams, setTreatmentParams] = useState(null);
    const [quantitiesByProductId, setQuantitiesByProductId] = useState({});
    const productIdsOptions = useMemo(() => currentAvailableProducts.map((x) => ({ value: x.id, label: x.name })), [currentAvailableProducts]);
    const currentProductIdsFieldName = diseaseIdToFieldName[currentDiseaseId];
    const currentSelectedProductIds = productIds[currentProductIdsFieldName];
    const currentSelectedProducts = currentAvailableProducts.filter((x) => currentSelectedProductIds.includes(x.id));
    const optidoseByProductId = useProductIdMap(optidoseRequest.data?.doses);
    const getAllSelectedProducts = useCallback((isOptidose = false) => ALL_DISEASE_IDS.flatMap((diseaseId) => {
        return productIds[diseaseIdToFieldName[diseaseId]].map((productId) => ({
            diseaseId,
            productId,
            ...(isOptidose
                ? {}
                : {
                    quantity: quantitiesByProductId[productId]
                })
        }));
    }), [productIds, quantitiesByProductId]);
    useEffect(() => {
        if (treatmentParams &&
            (!isEmpty(productIds.oidiumProductIds) || !isEmpty(productIds.mildewProductIds))) {
            const optidosePayload = {
                products: getAllSelectedProducts(true),
                ...treatmentParams
            };
            optidoseMutation(optidosePayload);
        }
    }, [treatmentParams, productIds]);
    const [createTreatmentMutation, createTreatmentRequest] = useMutation(client.decitrait.createTreatment(), {
        onSuccess: onCreateTreatmentSuccess,
        onError: onCreateTreatmentError
    });
    const hasNoSelectedProducts = productIds.mildewProductIds.length === 0 && productIds.oidiumProductIds.length === 0;
    const handleProductsSubmit = () => {
        if (treatmentParams && !isNil(plotId)) {
            const payload = {
                ...treatmentParams,
                plotId,
                products: getAllSelectedProducts()
            };
            createTreatmentMutation(payload);
        }
    };
    const handleTreatmentParamsSubmit = (values) => {
        setTreatmentParams(values);
        setStep(2);
    };
    const handleProductSelection = useCallback((productId) => {
        const { mildewProductIds, oidiumProductIds } = productIds;
        let finalOidiumIds = oidiumProductIds;
        let finalMildewIds = mildewProductIds;
        if (currentDiseaseId === DecitraitDiseaseId.mildew) {
            if (mildewProductIds.includes(productId)) {
                finalMildewIds = mildewProductIds.filter((id) => id !== productId);
            }
            else {
                finalMildewIds.push(productId);
            }
        }
        else {
            if (oidiumProductIds.includes(productId)) {
                finalOidiumIds = oidiumProductIds.filter((id) => id !== productId);
            }
            else {
                finalOidiumIds.push(productId);
            }
        }
        setProductIds({
            mildewProductIds: finalMildewIds,
            oidiumProductIds: finalOidiumIds
        });
    }, [productIds, currentDiseaseId, setProductIds]);
    const initialRequestsState = mergeQueryState(productsRequest, resourcesRequest);
    return {
        createTreatmentRequest,
        currentDiseaseId,
        currentSelectedProducts,
        handleProductsSubmit,
        handleTreatmentParamsSubmit,
        handleProductSelection,
        hasNoSelectedProducts,
        isInitialized: !initialRequestsState.isLoading,
        optidoseByProductId,
        productIds,
        productIdsOptions,
        quantitiesByProductId,
        sensibilityOptions,
        setCurrentDiseaseId,
        setQuantitiesByProductId,
        step
    };
};
